import React, { useState, useImperativeHandle, forwardRef } from "react";
import { COUNTRIES, CITIES_BY_COUNTRY, toIsoCountry } from "../../../utils/geo";
import { PlacesAutocomplete } from "../../transport/TransportModule";

/* ══ ESSENTIALS · coach-mark sheet body ═════════════════════
 *
 * Single-column stacked form inside the coach-mark sheet.
 * Only collects what signup didn't already grab:
 *   - currency        → SET_CURRENCY
 *   - preferred lang  → UPDATE_USER_PROFILE
 *   - primary loc     → ADD_LOC (only if workspace has no loc)
 *
 * The wizard calls save() via the forwarded ref when its
 * primary CTA is tapped — no inline submit.
 * ═══════════════════════════════════════════════════════════ */

const CURRENCIES = [
  { value: "SAR", labelEn: "SAR — Saudi Riyal",    labelAr: "ريال سعودي — SAR" },
  { value: "AED", labelEn: "AED — UAE Dirham",     labelAr: "درهم إماراتي — AED" },
  { value: "KWD", labelEn: "KWD — Kuwaiti Dinar",  labelAr: "دينار كويتي — KWD" },
  { value: "BHD", labelEn: "BHD — Bahraini Dinar", labelAr: "دينار بحريني — BHD" },
  { value: "QAR", labelEn: "QAR — Qatari Riyal",   labelAr: "ريال قطري — QAR" },
  { value: "OMR", labelEn: "OMR — Omani Rial",     labelAr: "ريال عماني — OMR" },
  { value: "USD", labelEn: "USD — US Dollar",      labelAr: "دولار أمريكي — USD" },
  { value: "EUR", labelEn: "EUR — Euro",           labelAr: "يورو — EUR" },
  { value: "GBP", labelEn: "GBP — British Pound",  labelAr: "جنيه إسترليني — GBP" },
];

const LOC_TYPES = [
  { value: "home",     labelEn: "Home",     labelAr: "المنزل" },
  { value: "office",   labelEn: "Office",   labelAr: "المكتب" },
  { value: "villa",    labelEn: "Villa",    labelAr: "فيلا" },
  { value: "compound", labelEn: "Compound", labelAr: "مجمع" },
  { value: "farm",     labelEn: "Farm",     labelAr: "مزرعة" },
  { value: "other",    labelEn: "Other",    labelAr: "أخرى" },
];

const EssentialsStep = forwardRef(function EssentialsStep(
  { state, dispatch, currentUser, platform, lang, isRTL },
  ref
) {
  const existingLoc = (state.locations || [])[0];
  const hd = state.householdData || {};
  const [currency, setCurrency] = useState(state.currency || "SAR");
  const [preferredLang, setPreferredLang] = useState(currentUser?.preferredLang || lang || "en");
  const [locName, setLocName] = useState(
    existingLoc?.name || (isRTL ? "المقر الرئيسي" : "Main Residence")
  );
  const [locCountry, setLocCountry] = useState(existingLoc?.country || hd.country || "Saudi Arabia");
  const [locCity, setLocCity] = useState(existingLoc?.city || hd.city || "");
  const [locType, setLocType] = useState(existingLoc?.type || "home");
  const [locAddress, setLocAddress] = useState(existingLoc?.address || "");
  const [locPlaceMeta, setLocPlaceMeta] = useState({
    placeId: existingLoc?.placeId || "",
    lat:     existingLoc?.lat ?? null,
    lng:     existingLoc?.lng ?? null,
    mapsUrl: existingLoc?.mapsUrl || "",
  });

  /* Resolve Maps credentials from the platform-configured API keys so the
     address field gets Places autocomplete (Google by default, Mapbox if
     the SA has switched providers). Falls back to a plain <input> when
     no key is configured. */
  const mapsProvider = platform?.platformApiKeys?.mapsProvider || "google";
  const mapsApiKey = mapsProvider === "mapbox"
    ? (platform?.platformApiKeys?.mapbox || "")
    : (platform?.platformApiKeys?.googleMaps || "");

  const cityOpts = (CITIES_BY_COUNTRY[locCountry] || []).map(c =>
    typeof c === "string" ? { value: c, label: c } : c
  );

  useImperativeHandle(ref, () => ({
    save() {
      if (currency && currency !== state.currency) {
        dispatch({ type: "SET_CURRENCY", currency });
      }
      if (currentUser?.id && preferredLang && preferredLang !== currentUser.preferredLang) {
        dispatch({
          type: "UPDATE_USER_PROFILE",
          id: currentUser.id,
          email: currentUser.email,
          phone: currentUser.phone,
          preferredLang,
        });
      }
      if (!existingLoc && locName.trim()) {
        dispatch({
          type: "ADD_LOC",
          loc: {
            id: `loc_${Date.now().toString(36)}`,
            name: locName.trim(),
            nameAr: "",
            country: locCountry,
            city: locCity,
            type: locType,
            isPrimary: true,
            address: locAddress.trim(),
            placeId: locPlaceMeta.placeId || "",
            lat: locPlaceMeta.lat ?? null,
            lng: locPlaceMeta.lng ?? null,
            mapsUrl: locPlaceMeta.mapsUrl || "",
            rooms: [],
          },
        });
      }
      return true;
    },
  }));

  const T = {
    currencyLabel: isRTL ? "عملة مساحة العمل" : "Workspace currency",
    langLabel:     isRTL ? "اللغة"               : "Language",
    locHeading:    isRTL ? "الموقع الرئيسي" : "Primary location",
    locHint: isRTL
      ? "يُستخدم لربط الموظفين والمركبات والبلاغات."
      : "Used to attach staff, vehicles and facility issues.",
    locName:    isRTL ? "اسم الموقع" : "Location name",
    locCountry: isRTL ? "الدولة"    : "Country",
    locCity:    isRTL ? "المدينة"   : "City",
    locType:    isRTL ? "النوع"     : "Type",
    locAddress: isRTL ? "العنوان"   : "Address",
    existing: isRTL
      ? "الموقع الرئيسي مسجّل مسبقاً — يمكن تعديله من الدليل."
      : "A primary location is already on file — edit it from Directory any time.",
  };

  return (
    <>
      {/* Currency + language on one row to save vertical space */}
      <div className="ob-field-row">
        <div className="ob-field">
          <label className="ob-field-label">{T.currencyLabel}</label>
          <select className="ob-select" value={currency} onChange={e => setCurrency(e.target.value)}>
            {CURRENCIES.map(c => (
              <option key={c.value} value={c.value}>{isRTL ? c.labelAr : c.labelEn}</option>
            ))}
          </select>
        </div>
        <div className="ob-field">
          <label className="ob-field-label">{T.langLabel}</label>
          <select className="ob-select" value={preferredLang} onChange={e => setPreferredLang(e.target.value)}>
            <option value="en">English</option>
            <option value="ar">العربية</option>
          </select>
        </div>
      </div>

      <div className="ob-sub-head">{T.locHeading}</div>
      <div className="ob-sub-hint">{existingLoc ? T.existing : T.locHint}</div>

      {!existingLoc && (
        <>
          <div className="ob-field">
            <label className="ob-field-label">{T.locName}</label>
            <input
              className="ob-input"
              value={locName}
              onChange={e => setLocName(e.target.value)}
              autoCorrect="off"
              autoCapitalize="words"
            />
          </div>
          {/* Country + type on one row (city jumps to its own row because it becomes a dropdown when known, free-text when unknown) */}
          <div className="ob-field-row">
            <div className="ob-field">
              <label className="ob-field-label">{T.locCountry}</label>
              <select
                className="ob-select"
                value={locCountry}
                onChange={e => { setLocCountry(e.target.value); setLocCity(""); }}
              >
                {(COUNTRIES || []).map(c => {
                  const v = typeof c === "string" ? c : c.value;
                  const l = typeof c === "string" ? c : c.label;
                  return <option key={v} value={v}>{l}</option>;
                })}
              </select>
            </div>
            <div className="ob-field">
              <label className="ob-field-label">{T.locType}</label>
              <select className="ob-select" value={locType} onChange={e => setLocType(e.target.value)}>
                {LOC_TYPES.map(lt => (
                  <option key={lt.value} value={lt.value}>{isRTL ? lt.labelAr : lt.labelEn}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="ob-field-row">
            <div className="ob-field">
              <label className="ob-field-label">{T.locCity}</label>
              {cityOpts.length ? (
                <select className="ob-select" value={locCity} onChange={e => setLocCity(e.target.value)}>
                  <option value="">—</option>
                  {cityOpts.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              ) : (
                <input className="ob-input" value={locCity} onChange={e => setLocCity(e.target.value)} />
              )}
            </div>
            <div className="ob-field">
              <label className="ob-field-label">{T.locAddress}</label>
              {mapsApiKey ? (
                <PlacesAutocomplete
                  apiKey={mapsApiKey}
                  provider={mapsProvider}
                  country={toIsoCountry(locCountry)}
                  isRTL={isRTL}
                  value={locAddress}
                  placeholder={isRTL ? "ابحث عن العنوان..." : "Search address..."}
                  className="ob-input"
                  onChange={v => {
                    setLocAddress(v.address || "");
                    setLocPlaceMeta({
                      placeId: v.placeId || "",
                      lat: v.lat ?? null,
                      lng: v.lng ?? null,
                      mapsUrl: v.mapsUrl || "",
                    });
                  }}
                />
              ) : (
                <input
                  className="ob-input"
                  value={locAddress}
                  onChange={e => setLocAddress(e.target.value)}
                  placeholder={isRTL ? "اختياري" : "Optional"}
                />
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
});

export default EssentialsStep;
